import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firestore, storage } from './firebaseConfig';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { createTeam as createTeamRecord, joinOrRequestTeam } from '../services/teamService';
import { createTournament as createTournamentRecord, joinTournamentAsPlayer } from '../services/tournamentService';
import DiscoveryCard from '../components/discovery/DiscoveryCard';
import './BucksHouseView.css';

const TEAM_FORMATS = ['8-Ball', '9-Ball', '10-Ball', 'Straight Pool', 'One Pocket', 'Bank Pool'];
const DISCIPLINES = ['EIGHT_BALL', 'NINE_BALL', 'TEN_BALL', 'STRAIGHT_POOL', 'ONE_POCKET', 'BANK_POOL'];
const STATUS_JOINABLE = new Set(['OPEN', 'CHECK_IN']);
const AMERICAS_COUNTRIES = [
  ['US', 'United States'], ['CA', 'Canada'], ['MX', 'Mexico'], ['PR', 'Puerto Rico'],
  ['VI', 'U.S. Virgin Islands'], ['GU', 'Guam'], ['AS', 'American Samoa'], ['MP', 'Northern Mariana Islands'],
  ['AI', 'Anguilla'], ['AG', 'Antigua and Barbuda'], ['AW', 'Aruba'], ['BS', 'Bahamas'],
  ['BB', 'Barbados'], ['BZ', 'Belize'], ['BM', 'Bermuda'], ['BQ', 'Bonaire, Sint Eustatius and Saba'],
  ['VG', 'British Virgin Islands'], ['KY', 'Cayman Islands'], ['CR', 'Costa Rica'], ['CU', 'Cuba'],
  ['CW', 'Curaçao'], ['DM', 'Dominica'], ['DO', 'Dominican Republic'], ['SV', 'El Salvador'],
  ['GD', 'Grenada'], ['GL', 'Greenland'], ['GP', 'Guadeloupe'], ['GT', 'Guatemala'],
  ['HT', 'Haiti'], ['HN', 'Honduras'], ['JM', 'Jamaica'], ['MQ', 'Martinique'],
  ['MS', 'Montserrat'], ['NI', 'Nicaragua'], ['PA', 'Panama'], ['BL', 'Saint Barthélemy'],
  ['KN', 'Saint Kitts and Nevis'], ['LC', 'Saint Lucia'], ['MF', 'Saint Martin'], ['PM', 'Saint Pierre and Miquelon'],
  ['VC', 'Saint Vincent and the Grenadines'], ['SX', 'Sint Maarten'], ['TT', 'Trinidad and Tobago'], ['TC', 'Turks and Caicos Islands'],
  ['AR', 'Argentina'], ['BO', 'Bolivia'], ['BR', 'Brazil'], ['CL', 'Chile'], ['CO', 'Colombia'],
  ['EC', 'Ecuador'], ['FK', 'Falkland Islands'], ['GF', 'French Guiana'], ['GY', 'Guyana'],
  ['PY', 'Paraguay'], ['PE', 'Peru'], ['SR', 'Suriname'], ['UY', 'Uruguay'], ['VE', 'Venezuela']
];

const normalizeText = (value) => String(value || '').trim();
const pretty = (value) =>
  normalizeText(value)
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getProfileName = (profile, user) =>
  normalizeText(profile?.nickname) ||
  normalizeText(profile?.name) ||
  normalizeText(profile?.displayName) ||
  'Pool Player';


const uploadDiscoveryBanner = async (file, path) => {
  if (!file) return '';
  if (!file.type.startsWith('image/')) throw new Error('Choose a valid image file.');
  if (file.size > 8 * 1024 * 1024) throw new Error('Images must be 8 MB or smaller.');
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storageRef = ref(storage, `${path}/${Date.now()}_${safeName}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
};

const readTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return 0;
};

function AuthNotice({ user, profileComplete }) {
  if (!user) {
    return (
      <div className="p4r-bucks-notice">
        <strong>Sign in required.</strong>
        <span>Teams, tournaments, and Race Room actions require an account.</span>
        <Link to="/login">Sign in</Link>
      </div>
    );
  }

  if (!profileComplete) {
    return (
      <div className="p4r-bucks-notice warning">
        <strong>Finish your player profile.</strong>
        <span>Add a nickname, bio, and profile image before using protected actions.</span>
        <Link to="/settings">Open settings</Link>
      </div>
    );
  }

  return null;
}

function TeamsPanel({ user, profile, profileComplete }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [mode, setMode] = useState('EXPLORE');
  const [formOpen, setFormOpen] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [form, setForm] = useState({ name:'', bio:'', primaryVenue:'', homeCountry:'US', visibility:'PUBLIC', joinPolicy:'OPEN', minRating:'', formats:['8-Ball'] });

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(firestore, 'teamRegistrations'),
      where('visibility', '==', 'PUBLIC'),
      where('status', '==', 'ACTIVE'),
      limit(60)
    );
    return onSnapshot(q, (snap) => {
      setTeams(snap.docs.map((item) => ({ id:item.id, ...item.data() }))
        .filter((team) => String(team.visibility || 'PUBLIC').toUpperCase() === 'PUBLIC')
        .sort((a,b) => readTimestamp(b.timeUpdated || b.timeCreated) - readTimestamp(a.timeUpdated || a.timeCreated)));
      setLoading(false);
    }, (err) => { console.error(err); setError('Unable to load teams right now.'); setLoading(false); });
  }, []);

  useEffect(() => () => { if (bannerPreview) URL.revokeObjectURL(bannerPreview); }, [bannerPreview]);
  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]:value }));
  const toggleFormat = (format) => setForm((prev) => ({ ...prev, formats:prev.formats.includes(format) ? prev.formats.filter((item) => item !== format) : [...prev.formats, format] }));
  const chooseBanner = (file) => { if (bannerPreview) URL.revokeObjectURL(bannerPreview); setBannerFile(file || null); setBannerPreview(file ? URL.createObjectURL(file) : ''); };

  const createTeam = async (event) => {
    event.preventDefault(); setError('');
    if (!user || !profileComplete) return setError('Sign in and complete your profile before creating a team.');
    if (normalizeText(form.name).length < 3 || normalizeText(form.bio).length < 10) return setError('Team name must be at least 3 characters and bio at least 10 characters.');
    setBusyId('create-team');
    try {
      const bannerUrl = await uploadDiscoveryBanner(bannerFile, `teamBanners/${user.uid}`);
      await createTeamRecord(user.uid, profile, { ...form, bannerUrl });
      setFormOpen(false); setBannerFile(null); setBannerPreview('');
      setForm({ name:'', bio:'', primaryVenue:'', homeCountry:'US', visibility:'PUBLIC', joinPolicy:'OPEN', minRating:'', formats:['8-Ball'] });
    } catch (err) { console.error(err); setError(err.message || 'Team creation failed.'); } finally { setBusyId(''); }
  };

  const joinTeam = async (team) => {
    setError('');
    if (!user || !profileComplete) return setError('Sign in and complete your player profile before joining a team.');
    setBusyId(team.id);
    try { await joinOrRequestTeam(team.id, user.uid); } catch (err) { setError(err.message || 'Unable to join or request this team.'); } finally { setBusyId(''); }
  };

  const visibleTeams = mode === 'MANAGER'
    ? teams.filter((team) => team.ownerId === user?.uid || (Array.isArray(team.admins) && team.admins.includes(user?.uid)))
    : teams;

  return <section className="p4r-bucks-section p4r-bucks-android-panel">
    <div className="p4r-bucks-subtabs" role="tablist" aria-label="Teams tools">
      <button type="button" className={mode === 'EXPLORE' ? 'active' : ''} onClick={() => { setMode('EXPLORE'); setFormOpen(false); }}>◉ Explorer</button>
      <button type="button" className={mode === 'MANAGER' ? 'active' : ''} onClick={() => setMode('MANAGER')}>◆ Manager</button>
    </div>
    <div className="p4r-bucks-section-head"><div><p className="p4r-kicker">{mode === 'MANAGER' ? 'Team manager' : 'Team explorer'}</p><h2>{mode === 'MANAGER' ? 'Your teams' : 'Find and join teams'}</h2></div>{mode === 'MANAGER' && <button className="p4r-bucks-primary" onClick={() => setFormOpen((v) => !v)}>{formOpen ? 'Close creator' : 'Create team'}</button>}</div>
    <AuthNotice user={user} profileComplete={profileComplete}/>{error && <div className="p4r-bucks-error">{error}</div>}
    {mode === 'MANAGER' && formOpen && <form className="p4r-bucks-form p4r-creator-form" onSubmit={createTeam}>
      <div className="p4r-creator-image"><label>Team banner <input type="file" accept="image/*" onChange={(e) => chooseBanner(e.target.files?.[0])}/></label><div className="p4r-creator-preview">{bannerPreview ? <img src={bannerPreview} alt="Team banner preview"/> : <span>Recommended: 1600 × 900, 16:9</span>}</div></div>
      <div className="p4r-bucks-form-grid">
        <label>Team name<input value={form.name} onChange={(e) => updateForm('name', e.target.value)} maxLength={40} required/></label>
        <label>Home venue / city<input value={form.primaryVenue} onChange={(e) => updateForm('primaryVenue', e.target.value)} maxLength={80}/></label>
        <label>Country<select value={form.homeCountry} onChange={(e) => updateForm('homeCountry', e.target.value)}>{AMERICAS_COUNTRIES.map(([code,label]) => <option value={code} key={code}>{label}</option>)}</select></label>
        <label>Join policy<select value={form.joinPolicy} onChange={(e) => updateForm('joinPolicy', e.target.value)}><option value="OPEN">Open joins</option><option value="APPROVAL_REQUIRED">Approval required</option></select></label>
        <label>Minimum rating / skill number<input type="number" min="0" value={form.minRating} onChange={(e) => updateForm('minRating', e.target.value)}/></label>
        <label className="p4r-bucks-wide">Bio<textarea value={form.bio} onChange={(e) => updateForm('bio', e.target.value)} maxLength={240} rows={4} required/></label>
      </div>
      <div className="p4r-chip-row">{TEAM_FORMATS.map((format) => <button type="button" className={form.formats.includes(format) ? 'selected' : ''} key={format} onClick={() => toggleFormat(format)}>{format}</button>)}</div>
      <button className="p4r-bucks-primary" disabled={busyId === 'create-team'}>{busyId === 'create-team' ? 'Creating...' : 'Create team'}</button>
    </form>}
    {loading ? <div className="p4r-bucks-empty">Loading teams...</div> : visibleTeams.length === 0 ? <div className="p4r-bucks-empty">{mode === 'MANAGER' ? 'No managed teams yet. Create a team to get started.' : 'No public teams yet.'}</div> : <div className="p4r-discovery-grid">{visibleTeams.map((team) => <DiscoveryCard key={team.id} to={`/bucks/teams/${team.id}`} image={team.bannerUrl} imageAlt={`${team.name} team banner`} eyebrow={team.joinPolicy === 'APPROVAL_REQUIRED' ? 'Approval required' : 'Open team'} title={team.name} description={team.bio} meta={[`${Number(team.membersCount || 0)} members`, team.primaryVenue || team.displayHome, Array.isArray(team.formats) ? team.formats.join(' • ') : '']} secondaryLabel={mode === 'MANAGER' ? 'Open team' : 'View team'} primaryAction={mode === 'MANAGER' ? undefined : { label:busyId === team.id ? 'Working...' : team.joinPolicy === 'APPROVAL_REQUIRED' ? 'Request' : 'Join', disabled:busyId === team.id || !user, onClick:() => joinTeam(team) }}/>)}</div>}
  </section>;
}

function TournamentsPanel({ user, profile, profileComplete }) {
  const [tournaments,setTournaments]=useState([]), [entriesByTournament,setEntriesByTournament]=useState({});
  const [mode,setMode]=useState('DISCOVER'), [formOpen,setFormOpen]=useState(false), [error,setError]=useState(''), [busyId,setBusyId]=useState('');
  const [bannerFile,setBannerFile]=useState(null), [bannerPreview,setBannerPreview]=useState('');
  const [form,setForm]=useState({ title:'', description:'', venueLabel:'', venueAddress:'', discipline:'EIGHT_BALL', entryMode:'INDIVIDUAL', maxEntries:16, raceTo:5, tableCount:1, entryFee:0, prizeSummary:'', city:'', state:'', country:'US', startAt:'', allowWalkIns:true, allowSelfScore:false });
  useEffect(() => onSnapshot(query(collection(firestore,'bucksTournaments'),orderBy('timeUpdated','desc'),limit(60)), snap => setTournaments(snap.docs.map((item)=>({id:item.id,...item.data()})).filter((item)=>item.status!=='DELETED'&&item.visibility!=='PRIVATE')), err=>{console.error(err);setError('Unable to load tournaments right now.');}),[]);
  useEffect(() => onSnapshot(query(collection(firestore,'bucksTournamentEntries'),limit(300)), snap => { const next={}; snap.docs.forEach((item)=>{const data=item.data(); if(data.tournamentId&&data.status!=='WITHDRAWN') next[data.tournamentId]=(next[data.tournamentId]||0)+1;});setEntriesByTournament(next);}),[]);
  useEffect(() => () => { if (bannerPreview) URL.revokeObjectURL(bannerPreview); }, [bannerPreview]);
  const updateForm=(field,value)=>setForm((prev)=>({...prev,[field]:value}));
  const chooseBanner=(file)=>{if(bannerPreview)URL.revokeObjectURL(bannerPreview);setBannerFile(file||null);setBannerPreview(file?URL.createObjectURL(file):'');};
  const createTournament=async(event)=>{event.preventDefault();setError('');if(!user||!profileComplete)return setError('Sign in and complete your profile before creating a tournament.');if(normalizeText(form.title).length<3)return setError('Tournament title must be at least 3 characters.');setBusyId('create-tournament');try{const bannerUrl=await uploadDiscoveryBanner(bannerFile,`tournamentBanners/${user.uid}`);await createTournamentRecord(user.uid,profile,{...form,venueName:form.venueLabel,bannerUrl},'OPEN');setFormOpen(false);setBannerFile(null);setBannerPreview('');setForm({title:'',description:'',venueLabel:'',venueAddress:'',discipline:'EIGHT_BALL',entryMode:'INDIVIDUAL',maxEntries:16,raceTo:5,tableCount:1,entryFee:0,prizeSummary:'',city:'',state:'',country:'US',startAt:'',allowWalkIns:true,allowSelfScore:false});}catch(err){setError(err.message||'Tournament creation failed.');}finally{setBusyId('');}};
  const joinTournament=async(tournament)=>{setError('');if(!user||!profileComplete)return setError('Sign in and complete your profile before joining a tournament.');if(!STATUS_JOINABLE.has(String(tournament.status||'OPEN').toUpperCase()))return setError('This tournament is not accepting new entries right now.');setBusyId(tournament.id);try{await joinTournamentAsPlayer({...tournament,status:String(tournament.status||'OPEN').toUpperCase(),entryMode:String(tournament.entryMode||'INDIVIDUAL').toUpperCase()},user.uid,profile);}catch(err){setError(err.message||'Unable to join this tournament.');}finally{setBusyId('');}};
  const visibleTournaments = mode === 'MANAGE'
    ? tournaments.filter((tournament) => tournament.managerUid === user?.uid)
    : tournaments;

  return <section className="p4r-bucks-section p4r-bucks-android-panel">
  <div className="p4r-bucks-subtabs" role="tablist" aria-label="Tournament tools">
    <button type="button" className={mode==='DISCOVER'?'active':''} onClick={()=>{setMode('DISCOVER');setFormOpen(false);}}>◉ Discover</button>
    <button type="button" className={mode==='MANAGE'?'active':''} onClick={()=>setMode('MANAGE')}>◆ Manage</button>
  </div>
  <div className="p4r-bucks-section-head"><div><p className="p4r-kicker">{mode==='MANAGE'?'Tournament manager':'Tournament discover'}</p><h2>{mode==='MANAGE'?'Your tournaments':'Discover and join tournaments'}</h2></div>{mode==='MANAGE'&&<button className="p4r-bucks-primary" onClick={()=>setFormOpen((v)=>!v)}>{formOpen?'Close creator':'Create tournament'}</button>}</div><AuthNotice user={user} profileComplete={profileComplete}/>{error&&<div className="p4r-bucks-error">{error}</div>}
  {mode==='MANAGE'&&formOpen&&<form className="p4r-bucks-form p4r-creator-form" onSubmit={createTournament}><div className="p4r-creator-image"><label>Tournament banner<input type="file" accept="image/*" onChange={(e)=>chooseBanner(e.target.files?.[0])}/></label><div className="p4r-creator-preview">{bannerPreview?<img src={bannerPreview} alt="Tournament banner preview"/>:<span>Recommended: 1600 × 900, 16:9</span>}</div></div><div className="p4r-bucks-form-grid">
  <label>Tournament title<input value={form.title} onChange={(e)=>updateForm('title',e.target.value)} maxLength={80} required/></label><label>Venue<input value={form.venueLabel} onChange={(e)=>updateForm('venueLabel',e.target.value)} maxLength={120}/></label><label className="p4r-bucks-wide">Description<textarea value={form.description} onChange={(e)=>updateForm('description',e.target.value)} maxLength={600} rows={4}/></label><label className="p4r-bucks-wide">Venue address<input value={form.venueAddress} onChange={(e)=>updateForm('venueAddress',e.target.value)} maxLength={180}/></label>
  <label>Discipline<select value={form.discipline} onChange={(e)=>updateForm('discipline',e.target.value)}>{DISCIPLINES.map((item)=><option value={item} key={item}>{pretty(item)}</option>)}</select></label><label>Entry mode<select value={form.entryMode} onChange={(e)=>updateForm('entryMode',e.target.value)}><option value="INDIVIDUAL">Singles</option><option value="TEAM">Teams</option><option value="BOTH">Singles or teams</option></select></label><label>Max entries<input type="number" min="2" max="256" value={form.maxEntries} onChange={(e)=>updateForm('maxEntries',e.target.value)}/></label><label>Race to<input type="number" min="1" max="99" value={form.raceTo} onChange={(e)=>updateForm('raceTo',e.target.value)}/></label><label>Tables<input type="number" min="1" max="99" value={form.tableCount} onChange={(e)=>updateForm('tableCount',e.target.value)}/></label><label>Entry fee<input type="number" min="0" value={form.entryFee} onChange={(e)=>updateForm('entryFee',e.target.value)}/></label><label>Start date/time<input type="datetime-local" value={form.startAt} onChange={(e)=>updateForm('startAt',e.target.value)}/></label><label>Prize summary<input value={form.prizeSummary} onChange={(e)=>updateForm('prizeSummary',e.target.value)} maxLength={120}/></label><label>City<input value={form.city} onChange={(e)=>updateForm('city',e.target.value)} maxLength={80}/></label><label>State / Region<input value={form.state} onChange={(e)=>updateForm('state',e.target.value)} maxLength={80}/></label><label>Country<select value={form.country} onChange={(e)=>updateForm('country',e.target.value)}>{AMERICAS_COUNTRIES.map(([code,label])=><option value={code} key={code}>{label}</option>)}</select></label><label className="p4r-check-label"><input type="checkbox" checked={form.allowWalkIns} onChange={(e)=>updateForm('allowWalkIns',e.target.checked)}/>Allow walk-ins</label><label className="p4r-check-label"><input type="checkbox" checked={form.allowSelfScore} onChange={(e)=>updateForm('allowSelfScore',e.target.checked)}/>Allow self scoring</label>
  </div><button className="p4r-bucks-primary" disabled={busyId==='create-tournament'}>{busyId==='create-tournament'?'Creating...':'Create tournament'}</button></form>}
  <div className="p4r-discovery-grid">{visibleTournaments.length===0?<div className="p4r-bucks-empty">{mode==='MANAGE'?'No managed tournaments yet. Create one to get started.':'No public tournaments yet.'}</div>:visibleTournaments.map((tournament)=>{const count=Number(entriesByTournament[tournament.id]||tournament.currentEntriesCount||0),max=Number(tournament.maxEntries||0);return <DiscoveryCard key={tournament.id} to={`/bucks/tournaments/${tournament.id}`} image={tournament.bannerUrl} imageAlt={`${tournament.title} tournament banner`} eyebrow={`${pretty(tournament.status||'OPEN')} • ${pretty(tournament.discipline||'EIGHT_BALL')}`} title={tournament.title} description={tournament.description||tournament.venueLabel||tournament.city||'Open tournament details'} meta={[`${count}${max?` / ${max}`:''} entries`,pretty(tournament.entryMode||'INDIVIDUAL'),`Race to ${tournament.raceTo||5}`,tournament.venueLabel||tournament.city]} secondaryLabel={mode==='MANAGE'?'Manage tournament':'View tournament'} primaryAction={mode==='MANAGE'?undefined:{label:busyId===tournament.id?'Joining...':'Join',disabled:busyId===tournament.id||!user,onClick:()=>joinTournament(tournament)}}/>;})}</div></section>;
}

const RACE_CUE_MODES = [
  ['EIGHT_BALL', '8-Ball'],
  ['NINE_BALL', '9-Ball'],
  ['TEN_BALL', '10-Ball'],
  ['THREE_CUSHION', '3-Cushion'],
  ['CLASSIC', 'Classic'],
  ['CUSTOM', 'Custom'],
];

const RACE_SCORING_STYLES = [
  ['RACKS', 'Racks'],
  ['POINTS_RACE', 'Points'],
  ['INNINGS', 'Points + innings'],
];

const RACE_SYNC_MODES = [
  ['LOCAL_ONLY', 'Local'],
  ['HOST_CONTROL', 'Host'],
  ['SHARED_CONTROL', 'Shared'],
  ['CONFIRM_SCORES', 'Confirm'],
];

const TIMER_STYLES = [
  ['OFF', 'Off'],
  ['GENTLE_RHYTHM', 'Gentle'],
  ['FOCUS_TIMER', 'Focus'],
  ['PRESSURE_TIMER', 'Pressure'],
];


const asArray = (value) => Array.isArray(value) ? value : [];
const clampScore = (value) => Math.max(0, Number(value || 0));
const raceTargetFor = (race, side) => Number(side === 'A' ? race?.raceToA || race?.raceTo : race?.raceToB || race?.raceTo) || 5;
const sideLabel = (side) => side === 'A' ? 'PLAYER_A' : side === 'B' ? 'PLAYER_B' : 'NONE';
const raceEventLabel = (type) => pretty(type || '').replace('Added', '').replace('Race ', '');

function makeRaceEvent(type, payload = {}, user) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    createdAtMs: Date.now(),
    createdByUid: user?.uid || '',
    ...payload,
  };
}

function trimRaceEvents(events, nextEvent) {
  return [...asArray(events), nextEvent].slice(-120);
}

function pushUndo(race, type) {
  return [...asArray(race?.undoStack), {
    type,
    createdAtMs: Date.now(),
    scoreA: clampScore(race?.scoreA),
    scoreB: clampScore(race?.scoreB),
    activeSide: race?.activeSide || 'NONE',
    inningsA: clampScore(race?.inningsA),
    inningsB: clampScore(race?.inningsB),
    postRaceInningsA: clampScore(race?.postRaceInningsA),
    postRaceInningsB: clampScore(race?.postRaceInningsB),
    currentRunA: clampScore(race?.currentRunA),
    currentRunB: clampScore(race?.currentRunB),
    highRunA: clampScore(race?.highRunA),
    highRunB: clampScore(race?.highRunB),
    modeStepsA: clampScore(race?.modeStepsA),
    modeStepsB: clampScore(race?.modeStepsB),
    totalRacks: clampScore(race?.totalRacks),
    raceWinsA: clampScore(race?.raceWinsA),
    raceWinsB: clampScore(race?.raceWinsB),
    recordedRaceScoreA: clampScore(race?.recordedRaceScoreA),
    recordedRaceScoreB: clampScore(race?.recordedRaceScoreB),
    status: race?.status || 'LIVE',
    resultRecorded: Boolean(race?.resultRecorded),
  }].slice(-30);
}

function RaceStat({ label, value }) {
  return (
    <span className="p4r-race-stat">
      <strong>{value}</strong>
      <small>{label}</small>
    </span>
  );
}

// eslint-disable-next-line no-unused-vars
function RaceRoomPanel({ user, profile }) {
  const [races, setRaces] = useState([]);
  const [activeRaceId, setActiveRaceId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSetup, setShowSetup] = useState(true);
  const [showSessions, setShowSessions] = useState(false);
  const [raceTab, setRaceTab] = useState('PLAY');
  const [fullscreenDisplay, setFullscreenDisplay] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [form, setForm] = useState({
    playerBName: 'Guest Player',
    raceToA: 5,
    raceToB: 5,
    cueMode: 'EIGHT_BALL',
    scoringStyle: 'RACKS',
    syncMode: 'LOCAL_ONLY',
    timerStyle: 'OFF',
    timerDurationSeconds: 30,
    doublesEnabled: false,
    playerAPartnerName: '',
    playerBPartnerName: '',
  });

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) {
      setRaces([]);
      return undefined;
    }

    const q = query(
      collection(firestore, 'quickRaces'),
      where('participantUids', 'array-contains', user.uid),
      limit(40)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => readTimestamp(b.timeUpdated || b.timeCreated) - readTimestamp(a.timeUpdated || a.timeCreated));
        setRaces(next);
        if (!activeRaceId && next.length > 0) setActiveRaceId(next[0].id);
      },
      (err) => {
        console.error('Race listener failed:', err);
        setError('Unable to load Race Room.');
      }
    );

    return () => unsub();
  }, [user, activeRaceId]);

  const activeRace = useMemo(() => races.find((race) => race.id === activeRaceId), [races, activeRaceId]);
  const lockedSetup = Boolean(activeRace && (clampScore(activeRace.scoreA) > 0 || clampScore(activeRace.scoreB) > 0 || asArray(activeRace.events).length > 1));
  const canEdit = Boolean(user && activeRace && (activeRace.createdBy === user.uid || asArray(activeRace.participantUids).includes(user.uid)));
  const scoringLocked = Boolean(activeRace?.syncMode === 'HOST_CONTROL' && activeRace?.createdBy !== user?.uid);
  const currentTimer = useMemo(() => {
    if (!activeRace) return 0;
    const remaining = Number(activeRace.timerRemainingSeconds || 0);
    if (!activeRace.timerRunning || !activeRace.timerStartedAtMs) return remaining;
    return Math.max(0, remaining - Math.floor((nowTick - Number(activeRace.timerStartedAtMs)) / 1000));
  }, [activeRace, nowTick]);

  const currentMatchSeconds = useMemo(() => {
    if (!activeRace) return 0;
    const saved = Number(activeRace.matchElapsedSeconds || 0);
    if (!activeRace.matchClockRunning || !activeRace.matchClockStartedAtMs) return saved;
    return saved + Math.max(0, Math.floor((nowTick - Number(activeRace.matchClockStartedAtMs)) / 1000));
  }, [activeRace, nowTick]);

  const formatClock = (seconds) => {
    const safe = Math.max(0, Number(seconds || 0));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const secs = safe % 60;
    return hours > 0
      ? `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      : `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  const createRace = async (event) => {
    event.preventDefault();
    setError('');

    if (!user) {
      setError('Sign in required.');
      return;
    }

    setBusy(true);

    try {
      const playerAName = getProfileName(profile, user);
      const targetA = Math.max(1, Number(form.raceToA || 5));
      const targetB = Math.max(1, Number(form.raceToB || targetA));
      const timerDuration = form.timerStyle === 'OFF' ? 0 : Math.max(5, Number(form.timerDurationSeconds || 30));
      const firstEvent = makeRaceEvent('RACE_STARTED', { side: 'NONE', label: 'Race started' }, user);
      const ref = await addDoc(collection(firestore, 'quickRaces'), {
        createdBy: user.uid,
        participantUids: [user.uid],
        acceptedUids: [user.uid],
        scorekeeperUid: user.uid,
        playerAUid: user.uid,
        playerBUid: '',
        playerAName,
        playerBName: normalizeText(form.playerBName) || 'Guest Player',
        playerAPartnerName: normalizeText(form.playerAPartnerName),
        playerBPartnerName: normalizeText(form.playerBPartnerName),
        doublesEnabled: Boolean(form.doublesEnabled),
        scoreA: 0,
        scoreB: 0,
        recordedRaceScoreA: 0,
        recordedRaceScoreB: 0,
        raceWinsA: 0,
        raceWinsB: 0,
        raceTo: targetA,
        raceToA: targetA,
        raceToB: targetB,
        rackTarget: Math.max(targetA, targetB),
        totalRacks: 0,
        inningsA: 0,
        inningsB: 0,
        postRaceInningsA: 0,
        postRaceInningsB: 0,
        postRaceInningsTotal: 0,
        currentRunA: 0,
        currentRunB: 0,
        highRunA: 0,
        highRunB: 0,
        modeStepsA: 0,
        modeStepsB: 0,
        cueMode: form.cueMode,
        scoringStyle: form.scoringStyle,
        syncMode: form.syncMode,
        activeSide: 'NONE',
        status: 'LIVE',
        resultRecorded: false,
        isPaused: false,
        timerStyle: form.timerStyle,
        timerDurationSeconds: timerDuration,
        timerRemainingSeconds: timerDuration,
        timerRunning: false,
        matchElapsedSeconds: 0,
        matchClockRunning: false,
        events: [firstEvent],
        undoStack: [],
        timeCreated: serverTimestamp(),
        timeUpdated: serverTimestamp(),
      });
      setActiveRaceId(ref.id);
      setShowSetup(false);
      setShowSessions(false);
    } catch (err) {
      console.error('Create race failed:', err);
      setError(err.message || 'Unable to create Race Room session.');
    } finally {
      setBusy(false);
    }
  };

  const patchRace = async (patch, eventType, payload = {}) => {
    if (!activeRace || !user || !canEdit || scoringLocked) return;
    const nextEvent = makeRaceEvent(eventType, payload, user);
    try {
      await updateDoc(doc(firestore, 'quickRaces', activeRace.id), {
        ...patch,
        events: trimRaceEvents(activeRace.events, nextEvent),
        lastActionByUid: user.uid,
        lastActionAt: serverTimestamp(),
        timeUpdated: serverTimestamp(),
      });
    } catch (err) {
      console.error('Race update failed:', err);
      setError(err.message || 'Unable to update Race Room.');
    }
  };

  const setTurn = async (side) => {
    if (!activeRace || !user || !canEdit) return;
    await patchRace({ activeSide: sideLabel(side), undoStack: pushUndo(activeRace, 'TURN_CHANGED') }, 'TURN_CHANGED', {
      side: sideLabel(side),
      label: `${side === 'A' ? activeRace.playerAName : activeRace.playerBName} controls the table`,
    });
  };

  const addScore = async (side, delta = 1) => {
    if (!activeRace || !user || !canEdit || scoringLocked) return;
    const scoreField = side === 'A' ? 'scoreA' : 'scoreB';
    const runField = side === 'A' ? 'currentRunA' : 'currentRunB';
    const highRunField = side === 'A' ? 'highRunA' : 'highRunB';
    const stepField = side === 'A' ? 'modeStepsA' : 'modeStepsB';
    const nextScore = clampScore(activeRace[scoreField]) + delta;
    const safeScore = Math.max(0, nextScore);
    const nextRun = delta > 0 ? clampScore(activeRace[runField]) + delta : Math.max(0, clampScore(activeRace[runField]) - 1);
    const target = raceTargetFor(activeRace, side);
    const complete = safeScore >= target;

    await patchRace({
      [scoreField]: safeScore,
      [runField]: nextRun,
      [highRunField]: Math.max(clampScore(activeRace[highRunField]), nextRun),
      [stepField]: Math.max(0, clampScore(activeRace[stepField]) + (delta > 0 ? 1 : -1)),
      activeSide: sideLabel(side),
      totalRacks: activeRace.scoringStyle === 'RACKS' ? Math.max(0, clampScore(activeRace.totalRacks) + (delta > 0 ? 1 : -1)) : clampScore(activeRace.totalRacks),
      status: complete ? 'COMPLETED' : 'LIVE',
      resultRecorded: complete,
      undoStack: pushUndo(activeRace, delta > 0 ? 'SCORE_ADDED' : 'SCORE_REMOVED'),
    }, delta > 0 ? 'SCORE_ADDED' : 'SCORE_REMOVED', {
      side: sideLabel(side),
      value: delta,
      label: `${delta > 0 ? '+' : ''}${delta} ${activeRace.scoringStyle === 'RACKS' ? 'rack' : 'point'}`,
    });
  };

  const shotTaken = async () => {
    if (!activeRace || !user || !canEdit || scoringLocked) return;
    const side = activeRace.activeSide === 'PLAYER_B' ? 'B' : 'A';
    const inningField = side === 'A' ? 'inningsA' : 'inningsB';
    const postField = side === 'A' ? 'postRaceInningsA' : 'postRaceInningsB';
    const runField = side === 'A' ? 'currentRunA' : 'currentRunB';
    await patchRace({
      [inningField]: clampScore(activeRace[inningField]) + 1,
      [postField]: clampScore(activeRace[postField]) + 1,
      postRaceInningsTotal: clampScore(activeRace.postRaceInningsTotal) + 1,
      [runField]: 0,
      activeSide: side === 'A' ? 'PLAYER_B' : 'PLAYER_A',
      undoStack: pushUndo(activeRace, 'SHOT_TAKEN'),
    }, 'SHOT_TAKEN', { side: sideLabel(side), label: 'Shot recorded' });
  };

  const countRaceWin = async () => {
    if (!activeRace || !user || !canEdit || scoringLocked) return;
    const aWins = clampScore(activeRace.scoreA) >= raceTargetFor(activeRace, 'A');
    const bWins = clampScore(activeRace.scoreB) >= raceTargetFor(activeRace, 'B');
    if (!aWins && !bWins) return;
    const winner = aWins ? 'A' : 'B';
    await patchRace({
      raceWinsA: clampScore(activeRace.raceWinsA) + (winner === 'A' ? 1 : 0),
      raceWinsB: clampScore(activeRace.raceWinsB) + (winner === 'B' ? 1 : 0),
      recordedRaceScoreA: clampScore(activeRace.scoreA),
      recordedRaceScoreB: clampScore(activeRace.scoreB),
      scoreA: 0,
      scoreB: 0,
      currentRunA: 0,
      currentRunB: 0,
      inningsA: 0,
      inningsB: 0,
      postRaceInningsA: 0,
      postRaceInningsB: 0,
      postRaceInningsTotal: 0,
      totalRacks: 0,
      status: 'LIVE',
      resultRecorded: true,
      activeSide: winner === 'A' ? 'PLAYER_A' : 'PLAYER_B',
      undoStack: pushUndo(activeRace, 'RACE_WIN_COUNTED'),
    }, 'RACE_WIN_COUNTED', { side: sideLabel(winner), label: 'Race win counted' });
  };

  const togglePause = async () => {
    if (!activeRace || !user || !canEdit) return;
    const paused = !activeRace.isPaused;
    await patchRace({ isPaused: paused, status: paused ? 'LIVE' : 'LIVE', undoStack: pushUndo(activeRace, paused ? 'PAUSED' : 'RESUMED') }, paused ? 'PAUSED' : 'RESUMED', {
      side: 'NONE',
      label: paused ? 'Paused' : 'Resumed',
    });
  };

  const timerAction = async (action) => {
    if (!activeRace || !user || !canEdit) return;
    const duration = Math.max(0, Number(activeRace.timerDurationSeconds || form.timerDurationSeconds || 30));
    if (action === 'start') {
      await patchRace({ timerRunning: true, timerStartedAtMs: Date.now(), timerRemainingSeconds: currentTimer || duration }, 'TIMER_STARTED', { label: 'Start timered' });
    } else if (action === 'pause') {
      await patchRace({ timerRunning: false, timerRemainingSeconds: currentTimer }, 'TIMER_PAUSED', { label: 'Pause timerd' });
    } else {
      await patchRace({ timerRunning: false, timerStartedAtMs: 0, timerRemainingSeconds: duration }, 'TIMER_RESET', { label: 'Reset timer' });
    }
  };

  const matchClockAction = async (action) => {
    if (!activeRace || !user || !canEdit) return;
    if (action === 'start') {
      await patchRace({ matchClockRunning: true, matchClockStartedAtMs: Date.now() }, 'MATCH_CLOCK_STARTED', { label: 'Match clock started' });
    } else if (action === 'pause') {
      await patchRace({ matchClockRunning: false, matchClockStartedAtMs: 0, matchElapsedSeconds: currentMatchSeconds }, 'MATCH_CLOCK_PAUSED', { label: 'Match clock paused' });
    } else {
      await patchRace({ matchClockRunning: false, matchClockStartedAtMs: 0, matchElapsedSeconds: 0 }, 'MATCH_CLOCK_RESET', { label: 'Match clock reset' });
    }
  };

  const completeSession = async () => {
    if (!activeRace || !canEdit) return;
    if (!window.confirm('Save and finish this Race Room session?')) return;
    await patchRace({
      status: 'COMPLETED',
      resultRecorded: true,
      timerRunning: false,
      matchClockRunning: false,
      matchElapsedSeconds: currentMatchSeconds,
      completedAt: serverTimestamp(),
    }, 'RACE_COMPLETED', { label: 'Session completed' });
    setRaceTab('HISTORY');
  };

  const undoLast = async () => {
    if (!activeRace || !user || !canEdit || scoringLocked) return;
    const stack = asArray(activeRace.undoStack);
    const last = stack[stack.length - 1];
    if (!last) return;
    await patchRace({
      scoreA: clampScore(last.scoreA),
      scoreB: clampScore(last.scoreB),
      activeSide: last.activeSide || 'NONE',
      inningsA: clampScore(last.inningsA),
      inningsB: clampScore(last.inningsB),
      postRaceInningsA: clampScore(last.postRaceInningsA),
      postRaceInningsB: clampScore(last.postRaceInningsB),
      postRaceInningsTotal: clampScore(last.postRaceInningsA) + clampScore(last.postRaceInningsB),
      currentRunA: clampScore(last.currentRunA),
      currentRunB: clampScore(last.currentRunB),
      highRunA: clampScore(last.highRunA),
      highRunB: clampScore(last.highRunB),
      modeStepsA: clampScore(last.modeStepsA),
      modeStepsB: clampScore(last.modeStepsB),
      totalRacks: clampScore(last.totalRacks),
      raceWinsA: clampScore(last.raceWinsA),
      raceWinsB: clampScore(last.raceWinsB),
      recordedRaceScoreA: clampScore(last.recordedRaceScoreA),
      recordedRaceScoreB: clampScore(last.recordedRaceScoreB),
      status: last.status || 'LIVE',
      resultRecorded: Boolean(last.resultRecorded),
      undoStack: stack.slice(0, -1),
    }, 'SCORE_REJECTED', { label: 'Undo' });
  };

  const resetRace = async () => {
    if (!activeRace || !user || !canEdit || scoringLocked) return;
    const confirmed = window.confirm('Reset this Race Room score?');
    if (!confirmed) return;
    await patchRace({
      scoreA: 0,
      scoreB: 0,
      totalRacks: 0,
      inningsA: 0,
      inningsB: 0,
      postRaceInningsA: 0,
      postRaceInningsB: 0,
      postRaceInningsTotal: 0,
      currentRunA: 0,
      currentRunB: 0,
      modeStepsA: 0,
      modeStepsB: 0,
      activeSide: 'NONE',
      status: 'LIVE',
      resultRecorded: false,
      timerRunning: false,
      timerStartedAtMs: 0,
      timerRemainingSeconds: Number(activeRace.timerDurationSeconds || 0),
      undoStack: pushUndo(activeRace, 'RACE_RESET'),
    }, 'RACE_RESET', { label: 'Race reset' });
  };

  const updateOpenSetup = async (field, value) => {
    if (!activeRace || lockedSetup || !canEdit) return;
    const normalizedValue = field.includes('raceTo') || field.includes('timerDuration') ? Math.max(1, Number(value || 1)) : value;
    const patch = { [field]: normalizedValue };
    if (field === 'raceToA') patch.raceTo = normalizedValue;
    if (field === 'timerDurationSeconds') patch.timerRemainingSeconds = normalizedValue;
    await patchRace({ ...patch, undoStack: pushUndo(activeRace, 'SCORE_MODE_CHANGED') }, field === 'cueMode' ? 'GAME_MODE_CHANGED' : 'SCORE_MODE_CHANGED', { label: 'Setup updated' });
  };

  const latestEvents = asArray(activeRace?.events).slice(-8).reverse();
  const achieved = activeRace && (clampScore(activeRace.scoreA) >= raceTargetFor(activeRace, 'A') || clampScore(activeRace.scoreB) >= raceTargetFor(activeRace, 'B'));
  const scoreLabel = activeRace?.scoringStyle === 'RACKS' ? 'Rack' : 'Point';

  return (
    <section className="p4r-bucks-section p4r-race-room-shell">
      <div className="p4r-race-headerline">
        <div>
          <p className="p4r-kicker">Race Room</p>
          <h2>Race Room</h2>
        </div>
        <div className="p4r-race-header-actions">
          <button className="p4r-icon-pill" type="button" onClick={() => setFullscreenDisplay((prev) => !prev)} disabled={!activeRace}>{fullscreenDisplay ? 'Exit live view' : 'Live view'}</button>
          <button className="p4r-icon-pill" type="button" onClick={() => { setRaceTab('PLAY'); setShowSetup((prev) => !prev); }}>New race</button>
        </div>
      </div>

      <nav className="p4r-race-tabs" aria-label="Race Room sections">
        {[
          ['PLAY', 'Play'], ['SESSIONS', 'Sessions'], ['PLAYERS', 'Players'], ['FLOW', 'Flow'], ['ROOM', 'Live View'], ['HISTORY', 'History']
        ].map(([value, label]) => (
          <button type="button" className={raceTab === value ? 'active' : ''} onClick={() => { setRaceTab(value); if (value === 'SESSIONS') setShowSessions(true); if (value === 'ROOM' && activeRace) setFullscreenDisplay(true); }} key={value}>{label}</button>
        ))}
      </nav>

      {!user && <AuthNotice user={user} profileComplete={false} />}
      {error && <div className="p4r-bucks-error">{error}</div>}

      {raceTab === 'PLAY' && showSetup && (
        <form className="p4r-race-create p4r-race-create-pro" onSubmit={createRace}>
          <label>
            Opponent
            <input value={form.playerBName} maxLength={80} onChange={(e) => setForm((prev) => ({ ...prev, playerBName: e.target.value }))} />
          </label>
          <label>
            Game
            <select value={form.cueMode} onChange={(e) => setForm((prev) => ({ ...prev, cueMode: e.target.value, scoringStyle: e.target.value === 'THREE_CUSHION' ? 'INNINGS' : prev.scoringStyle }))}>
              {RACE_CUE_MODES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
            </select>
          </label>
          <label>
            Scoring
            <select value={form.scoringStyle} onChange={(e) => setForm((prev) => ({ ...prev, scoringStyle: e.target.value }))}>
              {RACE_SCORING_STYLES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
            </select>
          </label>
          <label>
            P1 target
            <input type="number" min="1" max="99" value={form.raceToA} onChange={(e) => setForm((prev) => ({ ...prev, raceToA: e.target.value }))} />
          </label>
          <label>
            P2 target
            <input type="number" min="1" max="99" value={form.raceToB} onChange={(e) => setForm((prev) => ({ ...prev, raceToB: e.target.value }))} />
          </label>
          <label>
            Sync
            <select value={form.syncMode} onChange={(e) => setForm((prev) => ({ ...prev, syncMode: e.target.value }))}>
              {RACE_SYNC_MODES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
            </select>
          </label>
          <label>
            Timer
            <select value={form.timerStyle} onChange={(e) => setForm((prev) => ({ ...prev, timerStyle: e.target.value }))}>
              {TIMER_STYLES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
            </select>
          </label>
          <label>
            Seconds
            <input type="number" min="5" max="300" value={form.timerDurationSeconds} onChange={(e) => setForm((prev) => ({ ...prev, timerDurationSeconds: e.target.value }))} />
          </label>
          <label className="p4r-race-toggle-row">
            <input type="checkbox" checked={form.doublesEnabled} onChange={(e) => setForm((prev) => ({ ...prev, doublesEnabled: e.target.checked }))} />
            Doubles
          </label>
          {form.doublesEnabled && (
            <>
              <label>
                P1 partner
                <input value={form.playerAPartnerName} maxLength={80} onChange={(e) => setForm((prev) => ({ ...prev, playerAPartnerName: e.target.value }))} />
              </label>
              <label>
                P2 partner
                <input value={form.playerBPartnerName} maxLength={80} onChange={(e) => setForm((prev) => ({ ...prev, playerBPartnerName: e.target.value }))} />
              </label>
            </>
          )}
          <button className="p4r-bucks-primary" disabled={busy || !user}>{busy ? 'Starting...' : 'Start'}</button>
        </form>
      )}

      {activeRace && showSetup && lockedSetup && (
        <div className="p4r-race-lockbar">Setup locked after scoring starts.</div>
      )}

      {raceTab === 'PLAYERS' && (
        <div className="p4r-race-tool-panel">
          <h3>Players at this table</h3>
          {activeRace ? (
            <div className="p4r-race-player-cards">
              <article><strong>{activeRace.playerAName || 'Player A'}</strong><span>{activeRace.playerAUid ? 'Pool 4 Real player' : 'Guest'}</span><small>{activeRace.playerAPartnerName || 'Singles'}</small></article>
              <article><strong>{activeRace.playerBName || 'Player B'}</strong><span>{activeRace.playerBUid ? 'Pool 4 Real player' : 'Guest'}</span><small>{activeRace.playerBPartnerName || 'Singles'}</small></article>
            </div>
          ) : <div className="p4r-bucks-empty">Start or open a session to see its players.</div>}
        </div>
      )}

      {raceTab === 'FLOW' && (
        <div className="p4r-race-tool-panel">
          <h3>Match flow</h3>
          {!activeRace ? <div className="p4r-bucks-empty">Open a session first.</div> : (
            <div className="p4r-race-flow-grid">
              <div><span>Match time</span><strong>{formatClock(currentMatchSeconds)}</strong><div className="p4r-inline-actions"><button onClick={() => matchClockAction(activeRace.matchClockRunning ? 'pause' : 'start')}>{activeRace.matchClockRunning ? 'Pause' : 'Start'}</button><button onClick={() => matchClockAction('reset')}>Reset</button></div></div>
              <div><span>Shot timer</span><strong>{activeRace.timerStyle === 'OFF' ? 'Off' : `${currentTimer}s`}</strong><div className="p4r-inline-actions"><button disabled={activeRace.timerStyle === 'OFF'} onClick={() => timerAction(activeRace.timerRunning ? 'pause' : 'start')}>{activeRace.timerRunning ? 'Pause' : 'Start'}</button><button disabled={activeRace.timerStyle === 'OFF'} onClick={() => timerAction('reset')}>Reset</button></div></div>
              <label>Synchronization<select value={activeRace.syncMode || 'LOCAL_ONLY'} onChange={(e) => patchRace({ syncMode: e.target.value }, 'SYNC_MODE_CHANGED', { label: 'Sync mode changed' })}>{RACE_SYNC_MODES.map(([value,label]) => <option value={value} key={value}>{label}</option>)}</select></label>
              <label>Table control<select value={activeRace.activeSide || 'NONE'} onChange={(e) => patchRace({ activeSide: e.target.value }, 'TURN_CHANGED', { label: 'Table control changed' })}><option value="NONE">Manual / none</option><option value="PLAYER_A">Player 1</option><option value="PLAYER_B">Player 2</option></select></label>
            </div>
          )}
        </div>
      )}

      {raceTab === 'HISTORY' && (
        <div className="p4r-race-tool-panel">
          <h3>Race history</h3>
          {races.length === 0 ? <div className="p4r-bucks-empty">No Race Room history yet.</div> : (
            <div className="p4r-race-history-list">{races.map((race) => <button key={race.id} onClick={() => { setActiveRaceId(race.id); setRaceTab('PLAY'); }}><strong>{race.playerAName} {clampScore(race.scoreA)} – {clampScore(race.scoreB)} {race.playerBName}</strong><span>{pretty(race.cueMode)} • {pretty(race.status)} • {formatClock(race.matchElapsedSeconds)}</span></button>)}</div>
          )}
        </div>
      )}

      <div className={`${showSessions || raceTab === 'SESSIONS' ? 'p4r-race-layout sessions-open' : 'p4r-race-layout'} ${!['PLAY','SESSIONS','ROOM'].includes(raceTab) ? 'p4r-race-layout-hidden' : ''} ${fullscreenDisplay ? 'p4r-race-fullscreen' : ''}`}>
        <aside className="p4r-race-sidebar">
          {races.length === 0 ? (
            <div className="p4r-bucks-empty">No sessions.</div>
          ) : races.map((race) => (
            <button
              className={race.id === activeRaceId ? 'active' : ''}
              key={race.id}
              onClick={() => { setActiveRaceId(race.id); if (window.innerWidth < 900) setShowSessions(false); }}
            >
              <strong>{race.playerAName || 'Player A'} vs {race.playerBName || 'Player B'}</strong>
              <span>{clampScore(race.scoreA)} - {clampScore(race.scoreB)} • {pretty(race.status || 'LIVE')}</span>
            </button>
          ))}
        </aside>

        <div className="p4r-scoreboard p4r-scoreboard-pro">
          {!activeRace ? (
            <div className="p4r-bucks-empty">Tap Setup, enter the opponent, then Start.</div>
          ) : (
            <>
              <div className="p4r-scoreboard-top p4r-scoreboard-top-pro">
                <span>{pretty(activeRace.cueMode || 'EIGHT_BALL')}</span>
                <strong>{pretty(activeRace.scoringStyle || 'RACKS')} • {raceTargetFor(activeRace, 'A')}{raceTargetFor(activeRace, 'A') !== raceTargetFor(activeRace, 'B') ? `/${raceTargetFor(activeRace, 'B')}` : ''}</strong>
                <span>{activeRace.isPaused ? 'Paused' : pretty(activeRace.status || 'LIVE')}</span>
              </div>

              {showSetup && activeRace && !lockedSetup && (
                <div className="p4r-race-live-setup">
                  <select value={activeRace.cueMode || 'EIGHT_BALL'} onChange={(e) => updateOpenSetup('cueMode', e.target.value)}>
                    {RACE_CUE_MODES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                  <select value={activeRace.scoringStyle || 'RACKS'} onChange={(e) => updateOpenSetup('scoringStyle', e.target.value)}>
                    {RACE_SCORING_STYLES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                  <input type="number" min="1" max="99" value={raceTargetFor(activeRace, 'A')} onChange={(e) => updateOpenSetup('raceToA', e.target.value)} />
                  <input type="number" min="1" max="99" value={raceTargetFor(activeRace, 'B')} onChange={(e) => updateOpenSetup('raceToB', e.target.value)} />
                </div>
              )}

              <div className="p4r-race-status-strip">
                <RaceStat label="P1 races" value={clampScore(activeRace.raceWinsA)} />
                <RaceStat label="Racks" value={clampScore(activeRace.totalRacks)} />
                <RaceStat label="Timer" value={activeRace.timerStyle === 'OFF' ? 'Off' : `${currentTimer}s`} />
                <RaceStat label="Match" value={formatClock(currentMatchSeconds)} />
                <RaceStat label="P2 races" value={clampScore(activeRace.raceWinsB)} />
              </div>

              <div className="p4r-score-grid p4r-score-grid-pro">
                {['A', 'B'].map((side) => {
                  const isA = side === 'A';
                  const name = isA ? activeRace.playerAName || 'Player A' : activeRace.playerBName || 'Player B';
                  const partner = isA ? activeRace.playerAPartnerName : activeRace.playerBPartnerName;
                  const score = isA ? activeRace.scoreA : activeRace.scoreB;
                  const target = raceTargetFor(activeRace, side);
                  const active = activeRace.activeSide === sideLabel(side);
                  const innings = isA ? activeRace.inningsA || activeRace.postRaceInningsA : activeRace.inningsB || activeRace.postRaceInningsB;
                  const highRun = isA ? activeRace.highRunA : activeRace.highRunB;
                  return (
                    <article className={active ? 'p4r-score-player active' : 'p4r-score-player'} key={side}>
                      <div className="p4r-player-topline">
                        <button type="button" onClick={() => setTurn(side)} disabled={!canEdit}>Control</button>
                        <span>{active ? 'At table' : `Race to ${target}`}</span>
                      </div>
                      <h3>{name}</h3>
                      {activeRace.doublesEnabled && <small>{partner || (isA ? 'Partner 1' : 'Partner 2')}</small>}
                      <div className="p4r-score-number">{clampScore(score)}</div>
                      <div className="p4r-score-actions">
                        <button type="button" onClick={() => addScore(side, 1)} disabled={!canEdit || scoringLocked}>+ {scoreLabel}</button>
                        <button type="button" onClick={() => addScore(side, -1)} disabled={!canEdit || scoringLocked}>Undo</button>
                      </div>
                      <div className="p4r-player-microstats">
                        <span>Innings {clampScore(innings)}</span>
                        <span>High {clampScore(highRun)}</span>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="p4r-race-control-dock">
                <button type="button" onClick={shotTaken} disabled={!canEdit || scoringLocked}>Shot</button>
                {achieved && <button type="button" className="gold" onClick={countRaceWin} disabled={!canEdit || scoringLocked}>Next race</button>}
                <button type="button" onClick={togglePause} disabled={!canEdit}>{activeRace.isPaused ? 'Resume' : 'Pause'}</button>
                {activeRace.timerStyle !== 'OFF' && (
                  activeRace.timerRunning
                    ? <button type="button" onClick={() => timerAction('pause')} disabled={!canEdit}>Pause timer</button>
                    : <button type="button" onClick={() => timerAction('start')} disabled={!canEdit}>Start timer</button>
                )}
                {activeRace.timerStyle !== 'OFF' && <button type="button" onClick={() => timerAction('reset')} disabled={!canEdit}>Reset timer</button>}
                <button type="button" onClick={() => matchClockAction(activeRace.matchClockRunning ? 'pause' : 'start')} disabled={!canEdit}>{activeRace.matchClockRunning ? 'Pause clock' : 'Start clock'}</button>
                <button type="button" onClick={() => setFullscreenDisplay((prev) => !prev)}>Live display</button>
                <button type="button" onClick={completeSession} disabled={!canEdit}>Finish</button>
                <button type="button" onClick={undoLast} disabled={!canEdit || asArray(activeRace.undoStack).length === 0}>Undo</button>
                <button type="button" className="danger" onClick={resetRace} disabled={!canEdit || scoringLocked}>Reset</button>
              </div>

              <div className="p4r-race-event-strip">
                {latestEvents.length === 0 ? <span>No activity.</span> : latestEvents.map((item) => (
                  <span key={item.id || `${item.createdAtMs}-${item.type}`}>{raceEventLabel(item.type)} {item.label ? `• ${item.label}` : ''}</span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}


function BucksInboxPanel() {
  return (
    <section className="p4r-bucks-section p4r-bucks-inbox-panel">
      <div className="p4r-bucks-section-head">
        <div>
          <p className="p4r-kicker">Buck&apos;s House inbox</p>
          <h2>Invites and competitive updates</h2>
        </div>
      </div>
      <div className="p4r-bucks-empty p4r-bucks-inbox-empty">
        <strong>No Buck&apos;s House updates yet.</strong>
        <span>Team invitations, tournament invitations, announcements, match calls, and administrator alerts will appear here.</span>
      </div>
    </section>
  );
}

export default function BucksHouseView() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const explicitTab = searchParams.get('tab');
  const routeTab = explicitTab === 'tournaments' || location.pathname.includes('tournaments')
    ? 'tournaments'
    : explicitTab === 'inbox'
      ? 'inbox'
      : 'teams';
  const [activeTab, setActiveTab] = useState(routeTab);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => { setActiveTab(routeTab); }, [routeTab]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return undefined;
    }

    setProfileLoading(true);
    const unsub = onSnapshot(
      doc(firestore, 'userprofiles', user.uid),
      (snap) => {
        setProfile(snap.exists() ? snap.data() : null);
        setProfileLoading(false);
      },
      (err) => {
        console.error('Profile listener failed:', err);
        setProfile(null);
        setProfileLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  const profileComplete = Boolean(
    user &&
    profile &&
    normalizeText(profile.nickname) &&
    normalizeText(profile.bio) &&
    normalizeText(profile.profileImageUrl)
  );

  const openTab = (value) => {
    setActiveTab(value);
    const target = value === 'teams' ? '/bucks' : `/bucks?tab=${value}`;
    navigate(target);
  };

  const gated = profileLoading || !profileComplete;

  return (
    <main className="p4r-bucks-page p4r-bucks-v9-shell p4r-bucks-standalone">
      <Helmet>
        <title>Buck&apos;s House | Pool 4 Real</title>
        <meta
          name="description"
          content="Pool 4 Real Buck's House for teams, tournaments, invitations, announcements, and competitive billiards tools."
        />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <section className="p4r-bucks-topbar">
        <div className="p4r-bucks-title-wrap">
          <h1>Buck&apos;s House</h1>
          <p className="p4r-bucks-subtitle">Nearby teams, tournaments, and match-ready community tools</p>
        </div>

        <div className="p4r-bucks-actions">
          <Link className="p4r-bucks-settings" to="/settings" aria-label="Buck's House settings">⚙</Link>
        </div>
      </section>

      <nav className="p4r-bucks-tabs p4r-bucks-main-tabs" aria-label="Buck's House tools">
        {[
          ['teams', 'Teams'],
          ['tournaments', 'Tournaments'],
          ['inbox', 'Inbox'],
        ].map(([value, label]) => (
          <button
            type="button"
            className={activeTab === value ? 'active' : ''}
            onClick={() => openTab(value)}
            key={value}
          >
            {label}
          </button>
        ))}
      </nav>

      {gated ? (
        <section className="p4r-bucks-section p4r-bucks-gate-shell">
          <AuthNotice user={user} profileComplete={profileComplete} />
        </section>
      ) : (
        <section className="p4r-bucks-workspace" aria-live="polite">
          {activeTab === 'teams' && <TeamsPanel user={user} profile={profile} profileComplete={profileComplete} />}
          {activeTab === 'tournaments' && <TournamentsPanel user={user} profile={profile} profileComplete={profileComplete} />}
          {activeTab === 'inbox' && <BucksInboxPanel />}
        </section>
      )}
    </main>
  );
}
